/* @jsx h */
import {
  computed,
  defineComponent,
  h,
  nextTick,
  onMounted,
  ref,
  watch,
  type PropType,
} from "vue";
import type { GridColumnDef, GridSchema, CommitDir } from "./types";
import { useGridState } from "./useGridState";
import { CellViewer } from "./CellViewer";
import { CellEditor } from "./CellEditor";
import { BulkEditBar } from "./BulkEditBar";
import "./grid.css";

// ── Layout constants ─────────────────────────────────────────────
const DRAG_HANDLE_WIDTH = 20;
const CHECKBOX_COL_WIDTH = 44;
const ROW_HEIGHT = 38;
const HEADER_HEIGHT = 34;

// ── Flat-row helpers (tree mode) ─────────────────────────────────

export interface RowMeta {
  depth: number;
  parentId: string | number | null;
  hasChildren: boolean;
}

export interface FlatEntry {
  row: Record<string, any>;
  meta: RowMeta;
}

function flattenData(
  data: Record<string, any>[],
  schema: GridSchema,
  expandedIds: Set<string | number>,
): FlatEntry[] {
  if (!schema.childrenKey) {
    return data.map((row) => ({ row, meta: { depth: 0, parentId: null, hasChildren: false } }));
  }
  const pk = schema.primaryKey;
  const ck = schema.childrenKey;
  const result: FlatEntry[] = [];
  for (const row of data) {
    const rowId = row[pk] as string | number;
    const children = (row[ck] ?? []) as Record<string, any>[];
    const hasChildren = children.length > 0;
    result.push({ row, meta: { depth: 0, parentId: null, hasChildren } });
    if (hasChildren && expandedIds.has(rowId)) {
      for (const child of children) {
        result.push({ row: child, meta: { depth: 1, parentId: rowId, hasChildren: false } });
      }
    }
  }
  return result;
}

interface DropTarget {
  flatIdx: number;
  newIndex: number;
}

function getValidDropTargets(
  fromFlatIdx: number,
  entries: FlatEntry[],
): DropTarget[] {
  const { meta } = entries[fromFlatIdx];
  const targets: DropTarget[] = [];

  if (meta.depth === 0) {
    // Root rows: can drop at boundaries between top-level groups
    let newIndex = 0;
    targets.push({ flatIdx: 0, newIndex: 0 });
    let i = 0;
    while (i < entries.length) {
      if (entries[i].meta.depth === 0) {
        let j = i + 1;
        while (j < entries.length && entries[j].meta.depth > 0) j++;
        newIndex++;
        targets.push({ flatIdx: j, newIndex });
        i = j;
      } else {
        i++;
      }
    }
    // Remove no-op positions (before self and after self's group)
    let endOfGroup = fromFlatIdx + 1;
    while (endOfGroup < entries.length && entries[endOfGroup].meta.depth > 0) endOfGroup++;
    return targets.filter((t) => t.flatIdx !== fromFlatIdx && t.flatIdx !== endOfGroup);
  } else {
    // Child rows: can drop between siblings of the same parent
    const parentId = meta.parentId;
    let newIndex = 0;
    let inParent = false;
    for (let i = 0; i < entries.length; i++) {
      const m = entries[i].meta;
      if (m.depth === 1 && m.parentId === parentId) {
        if (!inParent) { targets.push({ flatIdx: i, newIndex: 0 }); inParent = true; }
        newIndex++;
        targets.push({ flatIdx: i + 1, newIndex });
      }
    }
    return targets.filter((t) => t.flatIdx !== fromFlatIdx && t.flatIdx !== fromFlatIdx + 1);
  }
}

function snapToTarget(rawIdx: number, targets: DropTarget[]): DropTarget | null {
  if (!targets.length) return null;
  return targets.reduce((best, t) =>
    Math.abs(t.flatIdx - rawIdx) < Math.abs(best.flatIdx - rawIdx) ? t : best,
  );
}

// ── Component ────────────────────────────────────────────────────

export const LemmonGrid = defineComponent({
  name: "LemmonGrid",
  props: {
    data: {
      type: Array as PropType<Record<string, any>[]>,
      required: true,
    },
    schema: {
      type: Object as PropType<GridSchema>,
      required: true,
    },
    changedRowIds: {
      type: Object as PropType<Set<string | number>>,
      default: () => new Set(),
    },
    validationErrors: {
      type: Object as PropType<Record<string | number, Record<string, string[]>>>,
      default: () => ({}),
    },
    onCellChange: {
      type: Function as PropType<(rowId: string | number, field: string, value: any) => void>,
      required: true,
    },
    onBulkChange: {
      type: Function as PropType<(rowIds: Set<string | number>, field: string, value: any) => void>,
      default: null,
    },
    onAddRow: {
      type: Function as PropType<() => void>,
      default: null,
    },
    onDeleteRows: {
      type: Function as PropType<(rowIds: Set<string | number>) => void>,
      default: null,
    },
    sortable: {
      type: Boolean,
      default: false,
    },
    onReorder: {
      type: Function as PropType<
        (draggedId: string | number, parentId: string | number | null, newIndex: number) => void
      >,
      default: null,
    },
  },
  setup(props) {
    const gridRef = ref<HTMLDivElement>();
    const scrollRef = ref<HTMLElement>();
    const headerCheckboxRef = ref<HTMLInputElement>();
    const state = useGridState();

    // ── Column sort ──────────────────────────────────────────────

    const sortKey = ref<string | null>(null);
    const sortDir = ref<"asc" | "desc" | null>(null);

    const handleHeaderClick = (col: GridColumnDef) => {
      if (sortKey.value === col.key) {
        if (sortDir.value === "asc") { sortDir.value = "desc"; }
        else { sortKey.value = null; sortDir.value = null; }
      } else {
        sortKey.value = col.key;
        sortDir.value = "asc";
      }
    };

    // ── Flat tree data (sorted root rows, children follow their parent) ──

    const flatData = computed(() => {
      let rootRows = props.data as Record<string, any>[];

      if (sortKey.value && sortDir.value) {
        const key = sortKey.value;
        const dir = sortDir.value;
        const col = props.schema.columns.find((c) => c.key === key);
        rootRows = [...rootRows].sort((a, b) => {
          const av = a[key];
          const bv = b[key];
          if (av == null && bv == null) return 0;
          if (av == null) return 1;
          if (bv == null) return -1;
          let cmp: number;
          if (col && (col.type === "number" || col.type === "price")) {
            cmp = Number(av) - Number(bv);
          } else if (col && col.type === "boolean") {
            cmp = Number(Boolean(av)) - Number(Boolean(bv));
          } else {
            cmp = String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: "base" });
          }
          return dir === "asc" ? cmp : -cmp;
        });
      }

      return flattenData(rootRows, props.schema, state.expandedIds.value);
    });

    // ── Layout ───────────────────────────────────────────────────

    const visibleCols = computed(() =>
      props.schema.columns.filter((c) => c.visible !== false),
    );

    const allRowIds = computed(() =>
      flatData.value.map((e) => e.row[props.schema.primaryKey] as string | number),
    );

    // Frozen cols start after the handle (when sortable) + checkbox
    const frozenOffset = computed(() => props.sortable ? DRAG_HANDLE_WIDTH : 0);

    const layout = computed(() => {
      let frozenLeft = frozenOffset.value + CHECKBOX_COL_WIDTH;
      let lastFrozenColIndex = -1;
      const cols = visibleCols.value.map((col, i) => {
        const left = col.frozen ? frozenLeft : undefined;
        if (col.frozen) {
          lastFrozenColIndex = i;
          frozenLeft += col.width ?? 120;
        }
        return { col, left };
      });
      return { cols, lastFrozenColIndex };
    });

    // ── Header checkbox sync ─────────────────────────────────────

    watch(
      () => state.selectedRowIds.value.size,
      () => {
        if (!headerCheckboxRef.value) return;
        const n = state.selectedRowIds.value.size;
        const total = allRowIds.value.length;
        headerCheckboxRef.value.checked = n > 0 && n === total;
        headerCheckboxRef.value.indeterminate = n > 0 && n < total;
      },
    );

    // Scroll active cell into view after navigation
    watch(
      () => state.activeCell.value,
      () => {
        if (!state.activeCell.value) return;
        nextTick(() => {
          const activeEl = gridRef.value?.querySelector(".cell-active") as HTMLElement | null;
          activeEl?.scrollIntoView({ block: "nearest", inline: "nearest" });
        });
      },
    );

    // ── Navigation helpers ───────────────────────────────────────

    const navigate = (dRow: number, dCol: number) => {
      if (!state.activeCell.value) return;
      const { rowIndex, colIndex } = state.activeCell.value;
      const newRow = Math.max(0, Math.min(flatData.value.length - 1, rowIndex + dRow));
      const newCol = Math.max(0, Math.min(visibleCols.value.length - 1, colIndex + dCol));
      state.setActive(newRow, newCol);
    };

    // ── Commit / cancel ──────────────────────────────────────────

    const handleCommit = (rowIndex: number, colIndex: number, value: any, dir: CommitDir) => {
      const col = visibleCols.value[colIndex];
      if (!col) return;
      const entry = flatData.value[rowIndex];
      if (!entry) return;
      const rowId = entry.row[props.schema.primaryKey] as string | number;
      const parsed = col.parser ? col.parser(String(value ?? "")) : value;
      props.onCellChange(rowId, col.key, parsed);
      state.stopEditing();
      if (dir === "down") navigate(1, 0);
      else if (dir === "right") navigate(0, 1);
      else if (dir === "left") navigate(0, -1);
      nextTick(() => gridRef.value?.focus());
    };

    const handleCancel = () => {
      state.stopEditing();
      nextTick(() => gridRef.value?.focus());
    };

    // ── Grid keyboard handler ────────────────────────────────────

    const handleGridKeydown = (e: KeyboardEvent) => {
      if (!state.activeCell.value) return;
      if (state.editingCell.value) return;

      const { rowIndex, colIndex } = state.activeCell.value;
      const col = visibleCols.value[colIndex];
      const entry = flatData.value[rowIndex];
      if (!entry) return;
      const row = entry.row;

      switch (e.key) {
        case "ArrowUp":    e.preventDefault(); navigate(-1, 0); break;
        case "ArrowDown":  e.preventDefault(); navigate(1, 0);  break;
        case "ArrowLeft":  e.preventDefault(); navigate(0, -1); break;
        case "ArrowRight": e.preventDefault(); navigate(0, 1);  break;
        case "Tab":
          e.preventDefault();
          navigate(0, e.shiftKey ? -1 : 1);
          break;
        case "Enter":
        case "F2":
          e.preventDefault();
          if (col?.editable && col.type === "boolean") {
            const rowId = row[props.schema.primaryKey] as string | number;
            props.onCellChange(rowId, col.key, row[col.key] ? 0 : 1);
          } else if (col?.editable && col.type !== "readonly") {
            state.startEditing(rowIndex, colIndex, row[col.key]);
          }
          break;
        case " ":
          if (col?.editable && col.type === "boolean") {
            e.preventDefault();
            const rowId = row[props.schema.primaryKey] as string | number;
            props.onCellChange(rowId, col.key, row[col.key] ? 0 : 1);
          }
          break;
        case "Escape":
          state.clearActive();
          break;
        case "Backspace":
        case "Delete":
          e.preventDefault();
          if (col?.editable && col.type !== "readonly") {
            const rowId = row[props.schema.primaryKey] as string | number;
            props.onCellChange(rowId, col.key, null);
          }
          break;
        default:
          if (
            e.key.length === 1 &&
            !e.ctrlKey &&
            !e.metaKey &&
            !e.altKey &&
            col?.editable &&
            col.type !== "readonly" &&
            col.type !== "boolean" &&
            col.type !== "color"
          ) {
            e.preventDefault();
            state.startEditing(rowIndex, colIndex, "", e.key);
          }
      }
    };

    // ── Cell interaction ─────────────────────────────────────────

    const handleCellClick = (rowIndex: number, colIndex: number, col: GridColumnDef) => {
      const entry = flatData.value[rowIndex];
      if (!entry) return;
      const row = entry.row;
      const rowId = row[props.schema.primaryKey] as string | number;

      if (col.type === "boolean" && col.editable) {
        props.onCellChange(rowId, col.key, row[col.key] ? 0 : 1);
        state.setActive(rowIndex, colIndex);
        nextTick(() => gridRef.value?.focus());
        return;
      }

      if (col.type === "color" && col.editable) {
        state.startEditing(rowIndex, colIndex, row[col.key]);
        return;
      }

      if (state.isActive(rowIndex, colIndex) && col.editable && col.type !== "readonly") {
        state.startEditing(rowIndex, colIndex, row[col.key]);
      } else {
        state.setActive(rowIndex, colIndex);
        nextTick(() => gridRef.value?.focus());
      }
    };

    const handleCellDblClick = (rowIndex: number, colIndex: number, col: GridColumnDef) => {
      if (!col.editable || col.type === "readonly") return;
      const entry = flatData.value[rowIndex];
      if (!entry) return;
      state.startEditing(rowIndex, colIndex, entry.row[col.key]);
    };

    // ── Drag-to-reorder ──────────────────────────────────────────

    const draggingFlatIdx = ref<number | null>(null);
    const dropFlatIdx = ref<number | null>(null);
    const activeDropTarget = ref<DropTarget | null>(null);

    const handleDragStart = (flatIdx: number, e: PointerEvent) => {
      if (!props.sortable || !props.onReorder) return;
      e.preventDefault();
      e.stopPropagation();

      const snapshot = flatData.value;
      const targets = getValidDropTargets(flatIdx, snapshot);
      draggingFlatIdx.value = flatIdx;

      const pointerToIdx = (clientY: number) => {
        const scroll = scrollRef.value;
        if (!scroll) return 0;
        const rect = scroll.getBoundingClientRect();
        const relY = clientY - rect.top - HEADER_HEIGHT + scroll.scrollTop;
        return Math.max(0, Math.min(snapshot.length, Math.round(relY / ROW_HEIGHT)));
      };

      const onMove = (me: PointerEvent) => {
        const snapped = snapToTarget(pointerToIdx(me.clientY), targets);
        activeDropTarget.value = snapped;
        dropFlatIdx.value = snapped?.flatIdx ?? null;
      };

      const onUp = () => {
        if (draggingFlatIdx.value !== null && activeDropTarget.value !== null) {
          const entry = snapshot[flatIdx];
          const draggedId = entry.row[props.schema.primaryKey] as string | number;
          props.onReorder!(draggedId, entry.meta.parentId, activeDropTarget.value.newIndex);
        }
        draggingFlatIdx.value = null;
        dropFlatIdx.value = null;
        activeDropTarget.value = null;
        document.removeEventListener("pointermove", onMove);
        document.removeEventListener("pointerup", onUp);
      };

      document.addEventListener("pointermove", onMove);
      document.addEventListener("pointerup", onUp);
    };

    // ── Bulk edit ────────────────────────────────────────────────

    const handleBulkApply = (field: string, value: any) => {
      props.onBulkChange?.(new Set(state.selectedRowIds.value), field, value);
    };

    const handleDeleteSelected = () => {
      props.onDeleteRows?.(new Set(state.selectedRowIds.value));
      state.clearSelection();
    };

    // ── Render ───────────────────────────────────────────────────

    return () => {
      const { cols, lastFrozenColIndex } = layout.value;
      const fo = frozenOffset.value;
      // Hide drag handles while a sort is active — sorted display order ≠ data order
      const hasDragHandle = props.sortable && sortKey.value === null;
      const hasTree = Boolean(props.schema.childrenKey);

      return (
        <div
          ref={gridRef}
          class="lg-grid"
          tabindex={-1}
          onKeydown={handleGridKeydown}
        >
          <div class="flex flex-1 overflow-hidden">
            <div ref={scrollRef} class="lg-scroll flex-1">
              <div class="lg-inner">

                {/* ── Header ── */}
                <div class="lg-header">
                  {hasDragHandle && (
                    <div
                      class="lg-th lg-drag-header frozen"
                      style={{ width: `${DRAG_HANDLE_WIDTH}px`, left: "0px" }}
                    />
                  )}
                  <div
                    class="lg-th col-checkbox frozen"
                    style={{ width: `${CHECKBOX_COL_WIDTH}px`, left: `${fo}px` }}
                  >
                    <input
                      ref={headerCheckboxRef}
                      type="checkbox"
                      class="checkbox checkbox-sm checkbox-primary"
                      onClick={() => state.setAllSelected(allRowIds.value)}
                    />
                  </div>
                  {cols.map(({ col, left }, ci) => {
                    const isSorted = sortKey.value === col.key;
                    return (
                      <div
                        key={col.key}
                        class={[
                          "lg-th lg-th-sortable",
                          col.frozen ? "frozen" : "",
                          ci === lastFrozenColIndex ? "frozen-shadow" : "",
                          isSorted ? "lg-th-sorted" : "",
                        ].join(" ")}
                        style={{
                          width: `${col.width ?? 120}px`,
                          ...(col.frozen ? { left: `${left}px` } : {}),
                        }}
                        onClick={() => handleHeaderClick(col)}
                      >
                        <span class="lg-th-label">{col.header}</span>
                        {isSorted ? (
                          <span class="material-symbols-outlined lg-sort-icon">
                            {sortDir.value === "asc" ? "arrow_upward" : "arrow_downward"}
                          </span>
                        ) : (
                          <span class="material-symbols-outlined lg-sort-icon lg-sort-icon--hint">
                            unfold_more
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* ── Rows ── */}
                {flatData.value.map(({ row, meta }, ri) => {
                  const rowId = row[props.schema.primaryKey] as string | number;
                  const isSelected = state.selectedRowIds.value.has(rowId);
                  const isChanged = props.changedRowIds.has(rowId);
                  const hasErrors = Boolean(props.validationErrors[rowId]);
                  const isDragging = draggingFlatIdx.value === ri;
                  const isExpanded = state.expandedIds.value.has(rowId);

                  return (
                    <div
                      key={`${meta.depth}-${String(rowId)}`}
                      class={[
                        "lg-row",
                        ri % 2 === 0 ? "row-even" : "row-odd",
                        isSelected ? "row-selected" : "",
                        isChanged && !hasErrors ? "row-changed" : "",
                        hasErrors ? "row-invalid" : "",
                        isDragging ? "row-dragging" : "",
                        meta.depth > 0 ? "row-child" : "",
                      ].join(" ")}
                    >
                      {/* Drag handle */}
                      {hasDragHandle && (
                        <div
                          class="lg-cell lg-drag-handle frozen"
                          style={{ width: `${DRAG_HANDLE_WIDTH}px`, left: "0px", zIndex: 3 }}
                          onPointerdown={(e) => handleDragStart(ri, e)}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <span class="material-symbols-outlined lg-drag-icon">
                            drag_indicator
                          </span>
                        </div>
                      )}

                      {/* Checkbox */}
                      <div
                        class="lg-cell col-checkbox frozen"
                        style={{ width: `${CHECKBOX_COL_WIDTH}px`, left: `${fo}px`, zIndex: 2 }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          class="checkbox checkbox-sm checkbox-primary"
                          checked={isSelected}
                          onClick={() => state.toggleRowSelection(rowId)}
                        />
                      </div>

                      {/* Data cells */}
                      {cols.map(({ col, left }, ci) => {
                        const active = state.isActive(ri, ci);
                        const editing = state.isEditing(ri, ci);
                        const cellErrors = props.validationErrors[rowId]?.[col.key] ?? [];
                        const cellValue = row[col.key];
                        const isTreeCol = hasTree && col.key === props.schema.treeColumnKey;

                        const extraClass = (() => {
                          if (typeof col.cellClass === "function") return col.cellClass(cellValue, row);
                          return col.cellClass ?? "";
                        })();

                        return (
                          <div
                            key={col.key}
                            class={[
                              "lg-cell",
                              col.frozen ? "frozen" : "",
                              ci === lastFrozenColIndex ? "frozen-shadow" : "",
                              active ? "cell-active" : "",
                              editing ? "cell-editing" : "",
                              cellErrors.length > 0 ? "cell-invalid" : "",
                              extraClass,
                            ].join(" ")}
                            style={{
                              width: `${col.width ?? 120}px`,
                              ...(col.frozen
                                ? { left: `${left}px`, zIndex: active || editing ? 12 : 2 }
                                : {}),
                            }}
                            title={cellErrors.join("\n") || undefined}
                            onClick={() => handleCellClick(ri, ci, col)}
                            onDblclick={() => handleCellDblClick(ri, ci, col)}
                          >
                            {editing ? (
                              <CellEditor
                                column={col}
                                value={state.editValue.value}
                                initialChar={state.initialChar.value}
                                onCommit={(v, dir) => handleCommit(ri, ci, v, dir)}
                                onCancel={handleCancel}
                              />
                            ) : isTreeCol ? (
                              <span
                                class="lg-cell-tree"
                                style={{ paddingLeft: `${meta.depth * 18}px` }}
                              >
                                {meta.hasChildren ? (
                                  <button
                                    class="lg-tree-chevron"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      state.toggleExpanded(rowId);
                                    }}
                                  >
                                    <span class="material-symbols-outlined">
                                      {isExpanded ? "expand_more" : "chevron_right"}
                                    </span>
                                  </button>
                                ) : (
                                  <span class="lg-tree-leaf" />
                                )}
                                <CellViewer column={col} value={cellValue} row={row} />
                              </span>
                            ) : (
                              <CellViewer column={col} value={cellValue} row={row} />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })}

                {/* Drop indicator */}
                {draggingFlatIdx.value !== null && dropFlatIdx.value !== null && (
                  <div
                    class="lg-drop-indicator"
                    style={{
                      top: `${HEADER_HEIGHT + dropFlatIdx.value * ROW_HEIGHT}px`,
                    }}
                  />
                )}

                {/* Empty state */}
                {props.data.length === 0 && (
                  <div class="flex items-center justify-center py-20 text-sm text-base-content/35">
                    No rows to display
                  </div>
                )}

                {/* Add row */}
                {props.onAddRow && (
                  <div class="lg-add-row">
                    <button
                      class="flex w-full items-center gap-1.5 px-4 py-2.5 text-xs text-base-content/40 transition-colors hover:text-primary"
                      onClick={props.onAddRow}
                    >
                      <span class="material-symbols-outlined text-sm">add</span>
                      Add row
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Bulk edit sidebar */}
            {state.selectedRowIds.value.size > 0 && (
              <BulkEditBar
                selectedCount={state.selectedRowIds.value.size}
                columns={visibleCols.value}
                onApply={handleBulkApply}
                onDeleteSelected={handleDeleteSelected}
                onClose={state.clearSelection}
              />
            )}
          </div>
        </div>
      );
    };
  },
});
